import type { ErrorCode } from '@pulseiptv/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const BASE = '/api/admin/v1';

async function request<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers: {
        // CSRF defence: the API rejects cookie-authenticated mutations without this header.
        'x-pulseiptv-csrf': '1',
        ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data as { error?: { code: ErrorCode; message: string; details?: unknown } } | null)?.error;
    if (res.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new Event('pulseiptv:unauthorized'));
    throw new ApiError(res.status, err?.code ?? 'INTERNAL', err?.message ?? res.statusText, err?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>('POST', path, form, true);
  },
  /** Upload with progress (0-1) for big files such as movies; abort() cancels. */
  uploadWithProgress: <T>(path: string, file: File, onProgress: (p: number) => void) => {
    const xhr = new XMLHttpRequest();
    const done = new Promise<T>((resolve, reject) => {
      xhr.open('POST', `${BASE}${path}`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('x-pulseiptv-csrf', '1');
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'The upload was interrupted'));
      xhr.onabort = () => reject(new ApiError(0, 'NETWORK', 'Upload cancelled'));
      xhr.onload = () => {
        let data: unknown = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          /* not JSON (e.g. the web server's 413 page) */
        }
        if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
        const err = (data as { error?: { code: ErrorCode; message: string; details?: unknown } } | null)?.error;
        reject(new ApiError(xhr.status, err?.code ?? 'INTERNAL', err?.message ?? (xhr.status === 413 ? 'The file is too large' : xhr.statusText || 'Upload failed'), err?.details));
      };
      const form = new FormData();
      form.append('file', file);
      xhr.send(form);
    });
    return { done, abort: () => xhr.abort() };
  },
};

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const details = Array.isArray(e.details)
      ? (e.details as { path?: string; message?: string }[]).map((d) => `${d.path ? d.path + ': ' : ''}${d.message}`).join('; ')
      : '';
    return details ? `${e.message} (${details})` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

// ─── View types returned by the admin API ───
export interface Me {
  user: { id: string; email: string; name: string; lastLoginAt?: string | null; passwordChangedAt?: string | null; createdAt?: string };
  organization: { id: string; name: string; timezone: string };
  role: 'OWNER' | 'ADMIN' | 'OPERATOR' | 'VIEWER';
  siteIds: string[];
  deploymentMode: string;
}

export interface Site {
  id: string;
  name: string;
  code: string;
  timezone: string;
  defaultLanguage: string;
  configVersion: number;
  branding: Record<string, unknown>;
  settings?: { fallback?: SiteFallback } & Record<string, unknown>;
  _count?: { rooms: number; devices: number; lineup: number };
}

export interface Zone {
  id: string;
  siteId: string;
  parentId: string | null;
  kind: 'BUILDING' | 'FLOOR' | 'AREA';
  name: string;
  sortOrder: number;
}

export interface Room {
  id: string;
  number: string;
  kind: string;
  zoneId: string | null;
  roomType: string | null;
  _count?: { devices: number };
}

export interface Asset {
  id: string;
  url: string;
  sha256: string;
}

export interface Category {
  id: string;
  key: string;
  name: string;
  sortOrder: number;
  imageAssetId?: string | null;
  image?: { storageKey: string } | null;
  color?: string | null;
  _count?: { channels: number };
}

export interface Channel {
  id: string;
  name: string;
  shortName: string | null;
  logoAssetId: string | null;
  artworkAssetId: string | null;
  logo: { storageKey: string } | null;
  artwork: { storageKey: string } | null;
  categories: { categoryId: string; isPrimary: boolean; category: Category }[];
  lineups: { siteId: string; number: number; sources?: { checkStatus: string | null; checkMessage: string | null; checkedAt: string | null }[] }[];
}

export interface Source {
  id?: string;
  protocol: string;
  host: string | null;
  port: number | null;
  url: string | null;
  priority: number;
  enabled: boolean;
  checkStatus?: string | null;
  checkMessage?: string | null;
  checkedAt?: string | null;
}

export interface LineupEntry {
  id: string;
  channelId: string;
  number: number;
  enabled: boolean;
  sortOrder: number;
  sources: Source[];
  channel: Channel;
  kind: 'STREAM' | 'LOCAL';
  playlist: PlaylistItem[];
  fallbackMode: FallbackMode | null;
  fallbackImageAssetId: string | null;
  fallbackImage: { storageKey: string } | null;
  fallbackVideoUrl: string | null;
  notes: string | null;
}

export type FallbackMode = 'NO_SIGNAL' | 'IMAGE' | 'VIDEO';
/** One video or picture of a local channel. assetUrl/bytes come back from the server for uploads. */
export interface PlaylistItem {
  type: 'VIDEO' | 'IMAGE';
  assetId?: string | null;
  url?: string | null;
  durationSec?: number | null;
  title?: string | null;
  assetUrl?: string | null;
  bytes?: number | null;
}
export interface SiteFallback {
  mode: FallbackMode;
  imageAssetId?: string | null;
  videoUrl?: string | null;
}

export interface Package {
  id: string;
  code: string;
  name: string;
  description: string | null;
  siteId: string | null;
  isDefault: boolean;
  channels: { channelId: string }[];
  _count: { assignments: number };
}

export interface Assignment {
  id: string;
  scopeType: 'SITE' | 'ZONE' | 'ROOM' | 'DEVICE';
  scopeId: string;
  package: { id: string; code: string; name: string };
}

export interface MenuItem {
  id: string;
  key: string;
  enabled: boolean;
  sortOrder: number;
  label: string | null;
  icon: string | null;
  placement: 'HOME' | 'SIDEBAR' | 'BOTH' | 'APPS';
  defaultLabel: string;
  defaultIcon: string;
  available: boolean;
}

export interface Device {
  id: string;
  name: string;
  siteId: string;
  roomId: string | null;
  room: { id: string; number: string } | null;
  platform: string;
  model: string | null;
  manufacturer: string | null;
  osVersion: string | null;
  serial: string | null;
  modelYear: number | null;
  appVersion: string | null;
  macAddress: string | null;
  ipAddress: string | null;
  status: 'ACTIVE' | 'DISABLED' | 'RETIRED';
  remoteLearning: boolean;
  cardPictures: boolean;
  smartPlug: string | null;
  wakeMac: string | null;
  activated: boolean;
  online: boolean;
  lastSeenAt: string | null;
  bootedAt: string | null;
  appliedConfigEtag: string | null;
  currentChannel: { id: string; number: number; name: string } | null;
}

export interface DeviceDetail extends Device {
  expectedConfigEtag: string;
  commands: { id: string; type: string; status: string; createdAt: string; sentAt?: string | null; ackedAt: string | null; result?: unknown }[];
  capabilities: Record<string, unknown>;
}

export const mediaUrl = (storageKey: string) => `/media/${storageKey}`;
